import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/src/shared';
import { Collapse } from '@savgroup-front-common/core/src/atoms/Collapse';
import { Form, FormGroup, RadioButtonGroup } from '@savgroup-front-common/core/src/atoms/Form';
import { Button } from '@savgroup-front-common/core/src/atoms/button';
import { toast } from '@savgroup-front-common/core/src/atoms/Toast';
import {
  SafeFormattedMessageWithoutSpread,
  safeFormattedIntlString,
} from '@savgroup-front-common/core/src/formatters';
import { RadioButtonHookForm, TextareaHookForm } from '@savgroup-front-common/core/src/molecules/Form';
import { ChevronDownIcon, CommentIcon } from '@savgroup-front-common/core/src/protons/icons';

import {
  getWorkflowActionsByFileId,
  type WorkflowActionSummary,
  type WorkflowTransitionSummary,
} from '../../data/dossierWorkflowMock';

import {
  $AccordionChevron,
  $AccordionHeader,
  $AccordionHeaderContent,
  $AccordionItem,
  $AccordionList,
  $ActionsContainer,
  $BlockCard,
  $ButtonGroup,
  $ChoicesSection,
  $DesktopChoices,
  $EmptyState,
  $FormBody,
  $StateDate,
  $StateLabel,
  $StateSelector,
  $StatesHeader,
  $StateTitle,
  $TransitionsList,
} from './DossierWorkflowBlock.styles';
import messages from './messages';

const FIELDS = {
  SELECTED_TARGET_STATE_NAME: 'selectedTargetStateName',
  COMMENT: 'comment',
  TO_CUSTOMER_COMMENT: 'toCustomerComment',
} as const;

interface StatusUpdateFormValues {
  [FIELDS.SELECTED_TARGET_STATE_NAME]: string;
  [FIELDS.COMMENT]: string;
  [FIELDS.TO_CUSTOMER_COMMENT]: string;
}

interface AvailableAction extends WorkflowTransitionSummary {
  text: string;
  value: string;
}

const getActionKey = (action: WorkflowActionSummary): string =>
  `${action.module.id}-${action.currentState.name}`;

const adaptTransitionsToAvailableActions = (
  transitions: WorkflowTransitionSummary[],
): AvailableAction[] =>
  transitions.map((transition) => ({
    ...transition,
    text: capitalize(transition.label),
    value: transition.targetState.name,
  }));

interface WorkflowActionPanelProps {
  action: WorkflowActionSummary;
}

const WorkflowActionPanel = ({ action }: WorkflowActionPanelProps) => {
  const [isCommentShown, setIsCommentShown] = useState(false);
  const [isCustomerCommentShown, setIsCustomerCommentShown] = useState(false);

  const availableActions = useMemo(
    () => adaptTransitionsToAvailableActions(action.transitions),
    [action.transitions],
  );

  const formContext = useForm<StatusUpdateFormValues>({
    defaultValues: {
      [FIELDS.SELECTED_TARGET_STATE_NAME]: '',
      [FIELDS.COMMENT]: '',
      [FIELDS.TO_CUSTOMER_COMMENT]: '',
    },
  });

  const { control, register, formState, handleSubmit, reset } = formContext;
  const { errors, isSubmitting } = formState;

  const selectedTargetStateName = useWatch({
    control,
    name: FIELDS.SELECTED_TARGET_STATE_NAME,
  });

  const selectedTransition = availableActions.find(
    ({ value }) => value === selectedTargetStateName,
  );

  const isCommentRequired = Boolean(selectedTransition?.isCommentMandatory);
  const canShowCustomerComment = Boolean(selectedTransition?.isCommentForCustomerAvailable);

  const handleResetComments = useCallback(() => {
    setIsCommentShown(false);
    setIsCustomerCommentShown(false);
    reset({
      [FIELDS.SELECTED_TARGET_STATE_NAME]: '',
      [FIELDS.COMMENT]: '',
      [FIELDS.TO_CUSTOMER_COMMENT]: '',
    });
  }, [reset]);

  const handleValidate = handleSubmit((values) => {
    const transition = availableActions.find(
      ({ value }) => value === values[FIELDS.SELECTED_TARGET_STATE_NAME],
    );

    if (!transition) {
      return;
    }

    toast.success(
      safeFormattedIntlString(messages.submitSuccess, {
        fromState: action.currentState.label,
        toState: transition.targetState.label,
      }),
      { autoClose: 5000, showIcon: true },
    );
    handleResetComments();
  });

  return (
    <$ChoicesSection>
      <Form formContext={formContext} onSubmit={handleValidate}>
        <$FormBody>
          {availableActions.length === 0 ? (
            <$EmptyState>
              <SafeFormattedMessageWithoutSpread message={messages.noAvailableAction} />
            </$EmptyState>
          ) : (
            <RadioButtonGroup
              name={FIELDS.SELECTED_TARGET_STATE_NAME}
              errors={errors}
              isRequired
            >
              <$TransitionsList>
                {availableActions.map((availableAction, index) => (
                  <RadioButtonHookForm
                    key={`status-update-${get(availableAction, 'value')}`}
                    name={FIELDS.SELECTED_TARGET_STATE_NAME}
                    tabIndex={index}
                    label={get(availableAction, 'text')}
                    value={get(availableAction, 'value')}
                    dataTestId={get(availableAction, 'value')}
                    register={register}
                    isFluid
                  />
                ))}
              </$TransitionsList>
            </RadioButtonGroup>
          )}

          <Collapse isOpen={Boolean(selectedTransition)}>
            {(isCommentRequired || isCommentShown) && (
              <FormGroup>
                <TextareaHookForm
                  name={FIELDS.COMMENT}
                  label={messages.comment}
                  register={register}
                  errors={errors}
                  isRequired={isCommentRequired}
                />
              </FormGroup>
            )}

            {isCustomerCommentShown && canShowCustomerComment && (
              <FormGroup>
                <TextareaHookForm
                  name={FIELDS.TO_CUSTOMER_COMMENT}
                  label={messages.customerComment}
                  register={register}
                  errors={errors}
                />
              </FormGroup>
            )}
          </Collapse>
        </$FormBody>

        {availableActions.length > 0 && (
          <$ActionsContainer>
            <$ButtonGroup>
              {canShowCustomerComment && (
                <Button
                  type={BUTTON_TYPES.BUTTON}
                  tertiary
                  icon={<CommentIcon />}
                  isPressed={isCustomerCommentShown}
                  dataTestId="add-customer-comment"
                  onClick={() => {
                    setIsCustomerCommentShown((previous) => !previous);
                  }}
                >
                  <SafeFormattedMessageWithoutSpread message={messages.fillCustomerComment} />
                </Button>
              )}

              {Boolean(selectedTransition) && !isCommentRequired && (
                <Button
                  type={BUTTON_TYPES.BUTTON}
                  tertiary
                  icon={<CommentIcon />}
                  isPressed={isCommentShown}
                  dataTestId="add-comment-to-status"
                  onClick={() => {
                    setIsCommentShown((previous) => !previous);
                  }}
                >
                  <SafeFormattedMessageWithoutSpread message={messages.fillComment} />
                </Button>
              )}

              <Button
                type={BUTTON_TYPES.SUBMIT}
                primary
                dataTestId="action-button-status-update"
                disabled={!selectedTransition}
                isLoading={isSubmitting}
              >
                <SafeFormattedMessageWithoutSpread message={messages.validate} />
              </Button>
            </$ButtonGroup>
          </$ActionsContainer>
        )}
      </Form>
    </$ChoicesSection>
  );
};

interface WorkflowStatusHeaderProps {
  action: WorkflowActionSummary;
}

const WorkflowStatusHeaderContent = ({ action }: WorkflowStatusHeaderProps) => {
  const intl = useIntl();

  return (
    <>
      <$StateDate>
        <SafeFormattedMessageWithoutSpread
          message={messages.stateDate}
          values={{
            date: intl.formatDate(action.stateDate, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            }),
          }}
        />
      </$StateDate>
      <$StateTitle>{action.currentState.shortLabel}</$StateTitle>
      <$StateLabel>{action.currentState.label}</$StateLabel>
    </>
  );
};

export interface DossierWorkflowBlockProps {
  fileId: string;
}

export const DossierWorkflowBlock = ({ fileId }: DossierWorkflowBlockProps) => {
  const actions = useMemo(() => getWorkflowActionsByFileId(fileId), [fileId]);
  const [currentActionIndex, setCurrentActionIndex] = useState(0);
  const [openActionKey, setOpenActionKey] = useState<string | undefined>(undefined);

  useEffect(() => {
    setCurrentActionIndex(0);
    setOpenActionKey(actions[0] ? getActionKey(actions[0]) : undefined);
  }, [actions, fileId]);

  const handleChangeCurrentAction = useCallback(
    (action: WorkflowActionSummary) => {
      const nextIndex = actions.findIndex(
        (candidate) =>
          candidate.module.id === action.module.id &&
          candidate.currentState.name === action.currentState.name,
      );

      setCurrentActionIndex(nextIndex >= 0 ? nextIndex : 0);
    },
    [actions],
  );

  const handleToggleAccordion = useCallback((action: WorkflowActionSummary) => {
    const key = getActionKey(action);

    setOpenActionKey((previous) => (previous === key ? undefined : key));
  }, []);

  if (actions.length === 0) {
    return null;
  }

  const currentAction = actions[currentActionIndex] ?? actions[0];
  const hasOneAction = actions.length === 1;

  return (
    <$BlockCard withoutMarginBottom dataTestId="dossier-workflow-block">
      {/* Desktop : onglets côte à côte (pattern Control New UI) */}
      <$StatesHeader>
        {actions.map((action) => {
          const isActive =
            currentAction.module.id === action.module.id &&
            currentAction.currentState.name === action.currentState.name;

          return (
            <$StateSelector
              key={`desktop-${getActionKey(action)}-${action.stateDate}`}
              type="button"
              $isActive={isActive}
              $hasOneAction={hasOneAction}
              onClick={() => handleChangeCurrentAction(action)}
              data-testid={`state_${action.currentState.shortLabel}`}
            >
              <WorkflowStatusHeaderContent action={action} />
            </$StateSelector>
          );
        })}
      </$StatesHeader>

      <$DesktopChoices>
        <WorkflowActionPanel
          key={`desktop-panel-${getActionKey(currentAction)}`}
          action={currentAction}
        />
      </$DesktopChoices>

      {/* Mobile : chaque statut est collapsable */}
      <$AccordionList>
        {actions.map((action) => {
          const actionKey = getActionKey(action);
          const isOpen = openActionKey === actionKey;

          return (
            <$AccordionItem key={`mobile-${actionKey}-${action.stateDate}`}>
              <$AccordionHeader
                type="button"
                $isOpen={isOpen}
                onClick={() => handleToggleAccordion(action)}
                aria-expanded={isOpen}
                data-testid={`accordion_${action.currentState.shortLabel}`}
              >
                <$AccordionHeaderContent>
                  <WorkflowStatusHeaderContent action={action} />
                </$AccordionHeaderContent>
                <$AccordionChevron $isOpen={isOpen}>
                  <ChevronDownIcon size="18px" />
                </$AccordionChevron>
              </$AccordionHeader>

              <Collapse isOpen={isOpen}>
                <WorkflowActionPanel
                  key={`mobile-panel-${actionKey}`}
                  action={action}
                />
              </Collapse>
            </$AccordionItem>
          );
        })}
      </$AccordionList>
    </$BlockCard>
  );
};

DossierWorkflowBlock.displayName = 'DossierWorkflowBlock';
